import type { ICellRendererParams } from 'ag-grid-community';
import type {
  PromptBrandAggregationBrand,
  PromptBrandAggregationProviderSlot,
} from '../types';
import { BrandFavicon } from '../components/BrandFavicon';
import { RelationshipBadge } from '../components/visibility/VisibilityBadges';
import { getHelpHtml } from '../utils/help/help-content';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

function PositionCell({ position, detected }: { position: number | null; detected: boolean }) {
  if (!detected || position == null) {
    return <span className="text-300">—</span>;
  }
  return (
    <span className="badge rounded-pill bg-primary text-white px-3 py-2">#{position}</span>
  );
}

function formatCoverage(brand: PromptBrandAggregationBrand): string {
  const denominator = brand.executedProviders > 0 ? brand.executedProviders : brand.totalProviders;
  return `${brand.seenInProviders}/${denominator}`;
}

export interface PromptProviderComparisonColumnCallbacks {
  slots: PromptBrandAggregationProviderSlot[];
  modelById: Map<string, { publicName?: string | null; iconSvg?: string | null } | undefined>;
  onAddCompetitor?: (brand: PromptBrandAggregationBrand) => void;
}

export function promptProviderComparisonColumnConfig(
  callbacks: PromptProviderComparisonColumnCallbacks,
) {
  const cols: Array<Record<string, unknown>> = [
    {
      field: '_logo',
      title: '',
      type: 'string',
      width: 48,
      maxWidth: 48,
      minWidth: 48,
      sortable: false,
      searchable: false,
      suppressMenu: true,
      suppressMovable: true,
      cellClass: 'd-flex align-items-center justify-content-center',
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 28, height: 28 }}
          >
            <BrandFavicon
              host={row.domain ?? row.detectedHost}
              domain={row.domain}
              faviconUrl={row.faviconUrl}
              name={row.canonicalName}
              relationship={row.relationship}
              brandType={row.brandType}
              colorIndex={params.node?.rowIndex ?? 0}
              size="sm"
            />
          </div>
        );
      },
    },
    {
      field: 'canonicalName',
      title: 'برند',
      tooltip: columnHelp('tables.brand'),
      type: 'string',
      flex: 1,
      minWidth: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) => {
        const row = params.data;
        if (!row) return null;
        return <span className="fw-medium text-body text-truncate">{row.canonicalName}</span>;
      },
    },
    {
      field: 'relationship',
      title: 'نوع',
      tooltip: columnHelp('tables.type'),
      type: 'string',
      flex: 1.35,
      minWidth: 136,
      sortable: false,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) =>
        params.data ? <RelationshipBadge relationship={params.data.relationship} solid /> : null,
    },
  ];

  for (const slot of callbacks.slots) {
    const model = callbacks.modelById.get(slot.modelId);
    const label = model?.publicName ?? slot.providerName;
    cols.push({
      field: `_slot_${slot.modelId}_${slot.providerId ?? 'pending'}`,
      title: label,
      type: 'string',
      flex: 0.8,
      minWidth: 72,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) => {
        const row = params.data;
        if (!row) return null;
        const cell = row.providers.find(entry => entry.modelId === slot.modelId) ?? null;
        return (
          <div className="d-flex justify-content-center align-items-center w-100 h-100">
            <PositionCell position={cell?.position ?? null} detected={cell?.detected ?? false} />
          </div>
        );
      },
      headerComponentParams: { tooltip: label },
    });
  }

  cols.push(
    {
      field: 'avgPosition',
      title: 'میانگین رتبه',
      tooltip: columnHelp('tables.avg_position'),
      type: 'string',
      flex: 1,
      minWidth: 96,
      searchable: true,
      valueFormatter: (p: { value: number | null }) => (p.value != null ? p.value.toFixed(1) : '—'),
    },
    {
      field: '_coverage',
      title: 'کاوریج',
      tooltip: columnHelp('metrics.coverage'),
      type: 'string',
      flex: 0.7,
      minWidth: 68,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) =>
        params.data ? <span>{formatCoverage(params.data)}</span> : null,
    },
    {
      field: 'visibilityScore',
      title: 'دیده‌شدن در AI',
      tooltip: columnHelp('metrics.ai_visibility'),
      type: 'number',
      flex: 1.1,
      minWidth: 130,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-pill bg-200 overflow-hidden flex-shrink-0"
              style={{ height: '0.375rem', width: '3.5rem' }}
            >
              <div
                className={`h-100 rounded-pill ${row.brandType === 'own_brand' ? 'bg-success' : 'bg-primary'}`}
                style={{ width: `${Math.min(Math.max(row.visibilityScore, 0), 100)}%` }}
              />
            </div>
            <span className="fs--1 text-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.visibilityScore}%
            </span>
          </div>
        );
      },
    },
  );

  if (callbacks.onAddCompetitor) {
    cols.push({
      field: '_actions',
      title: 'عملیات',
      tooltip: columnHelp('tables.actions'),
      type: 'string',
      flex: 1,
      minWidth: 100,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptBrandAggregationBrand>) => {
        const row = params.data;
        if (!row?.canAddAsCompetitor) {
          return <span className="fs--1 text-300">—</span>;
        }
        return (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              callbacks.onAddCompetitor?.(row);
            }}
            className="btn btn-sm btn-falcon-primary py-0 px-2 fs--2"
          >
            افزودن رقیب
          </button>
        );
      },
    });
  }

  return cols;
}
